/**
 * useMachineGridChrome.ts
 * The Machine Schedule grid's "chrome" behaviors, extracted from
 * MachineScheduleGrid.tsx (600-line cap, CODING-STANDARDS):
 *
 *   - Page-flow sticky header shim (Option 1 "flow sticky", Scenario embed):
 *     the grid scrolls horizontally in its own wrapper, so CSS sticky can't
 *     pin the header to the page's vertical scroll — a capture-phase scroll
 *     listener translates the header wrapper by the page overshoot, clamped so
 *     it releases (hands off) at the grid's bottom edge. Also keeps the ◀/▶
 *     paging-arrow overlay centred in the VISIBLE part of the grid (its own
 *     sticky top:50% only works in the bounded fillHeight mode).
 *   - ◀/▶ horizontal paging state (shared useHorizontalScrollState) — the grid
 *     can be very tall, so sideways paging must not require reaching the
 *     bottom scrollbar.
 *   - Ctrl+wheel Excel-like zoom (native non-passive listener — React's
 *     synthetic wheel handlers can't preventDefault the browser page-zoom).
 */

import { useCallback, useEffect, useRef } from 'react';
import { useHorizontalScrollState } from '../../../components/tables/gridFreeze';

export interface MachineGridChromeArgs {
  /** Viewport offset the header pins under (page-flow mode); undefined = bounded fillHeight mode. */
  pageFlowStickyTop?: number;
  /** Re-sync triggers: layout-affecting inputs. */
  totalDays: number;
  dayColWidth: number;
  rowHeight: number;
  /** Ctrl+wheel zoom callback (px delta); omit to disable wheel zoom. */
  onDayColWidthDelta?: (deltaPx: number) => void;
  /**
   * BUG-2026-0178 — when set, the ◀/▶ arrows never hide: paging while already
   * at that scroll edge calls this instead of scrolling, so the owner can grow
   * its date window ("scroll over in time as far as you need").
   */
  onPageBeyondEdge?: (dir: -1 | 1) => void;
}

export function useMachineGridChrome({
  pageFlowStickyTop,
  totalDays,
  dayColWidth,
  rowHeight,
  onDayColWidthDelta,
  onPageBeyondEdge,
}: MachineGridChromeArgs) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headerWrapRef = useRef<HTMLDivElement | null>(null);
  const arrowsRef = useRef<HTMLDivElement | null>(null);

  const {
    canScrollLeft: rawCanScrollLeft,
    canScrollRight: rawCanScrollRight,
    pageBy: scrollPageBy,
  } = useHorizontalScrollState(rootRef, true);

  // With an edge handler the arrows are always actionable: inside the window
  // they page the scroller; at (or without) an edge they hand off to the owner.
  const extendable = onPageBeyondEdge != null;
  const canScrollLeft = rawCanScrollLeft || extendable;
  const canScrollRight = rawCanScrollRight || extendable;
  const pageBy = useCallback(
    (dir: -1 | 1) => {
      const atEdge = dir < 0 ? !rawCanScrollLeft : !rawCanScrollRight;
      if (atEdge && onPageBeyondEdge) {
        onPageBeyondEdge(dir);
        return;
      }
      scrollPageBy(dir);
    },
    [rawCanScrollLeft, rawCanScrollRight, onPageBeyondEdge, scrollPageBy],
  );

  // Page-flow sticky shim + arrow positioning.
  useEffect(() => {
    if (pageFlowStickyTop == null) return;
    const sync = () => {
      const root = rootRef.current;
      const hw = headerWrapRef.current;
      if (!root || !hw) return;
      const r = root.getBoundingClientRect();
      const overshoot = pageFlowStickyTop - r.top;
      const max = Math.max(0, r.height - hw.offsetHeight);
      const y = Math.min(Math.max(overshoot, 0), max);
      hw.style.transform = y > 0 ? `translateY(${y}px)` : '';
      const arrows = arrowsRef.current;
      if (arrows) {
        const visTop = Math.max(r.top, pageFlowStickyTop);
        const visBottom = Math.min(r.bottom, window.innerHeight);
        // Centre of the VISIBLE slice of the grid, clamped inside the grid so
        // the buttons never poke past its bottom edge.
        const mid = Math.min(
          Math.max(0, (visTop + visBottom) / 2 - r.top),
          Math.max(0, r.height - 40),
        );
        arrows.style.transform = `translateY(${Math.round(mid)}px)`;
      }
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true, capture: true });
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', sync);
    };
  // canScroll flags re-run the sync so a freshly-(re)mounted arrows overlay is
  // positioned before the first scroll event.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFlowStickyTop, totalDays, dayColWidth, rowHeight, canScrollLeft, canScrollRight]);

  // Zoom / window changes alter scrollWidth without firing a scroll event —
  // nudge the arrows hook so they appear/disappear immediately.
  useEffect(() => {
    rootRef.current?.dispatchEvent(new Event('scroll'));
  }, [dayColWidth, totalDays]);

  // Ctrl+wheel Excel-like zoom.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !onDayColWidthDelta) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      onDayColWidthDelta(e.deltaY < 0 ? 2 : -2);
    };
    root.addEventListener('wheel', onWheel, { passive: false });
    return () => root.removeEventListener('wheel', onWheel);
  }, [onDayColWidthDelta]);

  return { rootRef, headerWrapRef, arrowsRef, canScrollLeft, canScrollRight, pageBy };
}
